import type { CropPosition, MediaAspectRatio } from "./types";

export const aspectRatioValues: Record<Exclude<MediaAspectRatio, "original">, number> = {
  "1:1": 1,
  "4:5": 4 / 5,
  "2:3": 2 / 3,
  "9:16": 9 / 16,
  "16:9": 16 / 9
};

export function getAspectRatioValue(aspectRatio: MediaAspectRatio) {
  return aspectRatio === "original" ? null : aspectRatioValues[aspectRatio];
}

export function getObjectPosition(cropPosition: CropPosition) {
  if (cropPosition === "top") return "center top";
  if (cropPosition === "bottom") return "center bottom";
  return "center center";
}

function loadImage(file: File) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Unable to load image for crop"));
    };
    image.src = url;
  });
}

export async function cropImageFile(file: File, aspectRatio: MediaAspectRatio, cropPosition: CropPosition) {
  const targetRatio = getAspectRatioValue(aspectRatio);
  if (!targetRatio) return file;

  const image = await loadImage(file);
  const sourceRatio = image.naturalWidth / image.naturalHeight;
  let sx = 0;
  let sy = 0;
  let sw = image.naturalWidth;
  let sh = image.naturalHeight;

  if (sourceRatio > targetRatio) {
    sw = Math.round(image.naturalHeight * targetRatio);
    sx = Math.round((image.naturalWidth - sw) / 2);
  } else if (sourceRatio < targetRatio) {
    sh = Math.round(image.naturalWidth / targetRatio);
    if (cropPosition === "top") sy = 0;
    else if (cropPosition === "bottom") sy = image.naturalHeight - sh;
    else sy = Math.round((image.naturalHeight - sh) / 2);
  }

  const canvas = document.createElement("canvas");
  canvas.width = sw;
  canvas.height = sh;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas is not available for crop");
  context.drawImage(image, sx, sy, sw, sh, 0, 0, sw, sh);

  const mimeType = file.type === "image/png" || file.type === "image/webp" ? file.type : "image/jpeg";
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((result) => (result ? resolve(result) : reject(new Error("Unable to create cropped image"))), mimeType, 0.92);
  });

  const extension = mimeType === "image/png" ? "png" : mimeType === "image/webp" ? "webp" : "jpg";
  const baseName = file.name.replace(/\.[^.]+$/, "") || "telegram-card";
  return new File([blob], `${baseName}-${aspectRatio.replace(":", "x")}.${extension}`, { type: mimeType });
}
